import { Checkbox, Col, Input, Row, Select } from 'antd'
import React, { useEffect, useState } from 'react'
import _ from 'lodash'

import './css/pool-scoring.css'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faSquare, faSuitcaseMedical } from '@fortawesome/free-solid-svg-icons'

import { squadronHelper, matchData, calculateResults } from '../../../../shared/pools'
import { attackType } from '../../../../shared/enums'
import { checkValidAttack } from '../../../../shared/scoring'

const PoolScoreMatrix = (props) => {
  const [pool, setPool] = useState(props.pool)
  const [scores, setScores] = useState([])
  const [focusedMatch, setFocusedMatch] = useState([-1, -2])
  const [errors, setErrors] = useState([])
  const [advancedStats, setAdvancedStats] = useState([])

  const [pointsSettings, setPointsSettings] = useState({})

  useEffect(() => {
    const maxMatchScore = props.config.score + 2
    const pointsPerWin = maxMatchScore * 2
    const numberFencers = props?.pool?.participants?.length
    const maxPoolScore = pointsPerWin * (numberFencers - 1) + maxMatchScore * (numberFencers - 1)

    setPointsSettings({
      maxMatchScore,
      pointsPerWin,
      numberFencers,
      maxPoolScore
    })
    // eslint-disable-next-line
  }, [props.config])

  useEffect(() => {
    if (props.pool) {
      let needToSendUpdate = false
      let _pool = props.pool
      let _scores
      let _advanced

      setPool(_pool)
      if (props?.scores?.scores) {
        setPool(props?.scores)
        setScores(props.scores?.scores)
        _pool = props?.scores
        _scores = props.scores?.scores
      } else if (props?.pool?.scores) {
        setPool(props.pool)
        setScores(props.pool.scores)
        _pool = props.pool
        _scores = props.pool.scores
      } else {
        const participantScores = new Array(props?.pool?.participants?.length).fill()
        const scoreList = new Array(props?.pool?.participants?.length).fill(participantScores)
        setScores(scoreList)
        _scores = scoreList
        needToSendUpdate = true
      }
      if (props?.scores?.advanced) {
        setAdvancedStats(props?.scores?.advanced)
        _advanced = props?.scores?.advanced
      } else if (props?.pool?.advanced) {
        setAdvancedStats(props?.pool?.advanced)
        _advanced = props?.pool?.advanced
      } else {
        const matchScoreAdvanced = new Array(props.config.score).fill()
        const adv = matchData[props?.pool?.participants?.length].map((match, index) => {
          const x = {}
          x[match[0]] = JSON.parse(JSON.stringify(matchScoreAdvanced))
          x[match[1]] = JSON.parse(JSON.stringify(matchScoreAdvanced))
          return x
        })
        setAdvancedStats(adv)
        _advanced = adv
        needToSendUpdate = true
      }

      if (needToSendUpdate) {
        props.onLiveUpdate(Object.assign({}, _pool, { scores: _scores }, { advanced: _advanced }))
      }
    }

    // eslint-disable-next-line
  }, [props.pool])

  const getMatchNumber = (a, b) => {
    const numberParticipants = pool?.participants?.length
    const matchNumber = matchData[numberParticipants].findIndex(f => (f[0] === a + 1 || f[0] === b + 1) && (f[1] === a + 1 || f[1] === b + 1))
    if (matchNumber < 0) {
      console.error(`${a} vs ${b} not found`)
    }

    return `M${matchNumber + 1}`
  }

  const isFocusedMatch = (row, column) => {
    let isFocused = false
    if (_.difference(focusedMatch, [row, column]).length === 0) isFocused = true
    return isFocused
  }

  const isErrored = (row, column) => {
    let hasError = false
    errors.forEach(e => {
      if ((e[0] === row || e[0] === column) && (e[1] === row || e[1] === column)) {
        hasError = true
      }
    })

    return hasError
  }

  const handleScore = (row, column, event) => {
    const maxScore = parseInt(props?.config?.score, 10)
    const currentScore = event.target.value === maxScore ? 'V' : event.target.value

    const newScores = JSON.parse(JSON.stringify(scores))
    const newErrors = JSON.parse(JSON.stringify(errors))
    let newPool = JSON.parse(JSON.stringify(pool))
    let hasError = false

    // clear errors
    const errorsToRemove = []
    newErrors.forEach((e, index) => {
      if ((e[0] === row || e[0] === column) && (e[1] === row || e[1] === column)) {
        errorsToRemove.push(index)
      }
    })

    errorsToRemove.sort().reverse()
    errorsToRemove.forEach(e => {
      newErrors.splice(e, 1)
    })

    if (currentScore.length > 0) {
      newScores[row][column] = currentScore.toUpperCase()

      if (_.isNaN(parseInt(currentScore, 10)) && _.first(currentScore.toLowerCase()) !== 'v') {
        newErrors.push([row, column])
        hasError = true
      } else if (currentScore > maxScore) {
        newErrors.push([row, column])
        hasError = true
      } else if (!_.isNil(newScores[column][row])) {
        if (_.first(currentScore.toString().toLowerCase()) === 'v' && _.first(newScores[column][row].toString().toLowerCase()) === 'v') {
          newErrors.push([row, column])
          hasError = true
        } if (currentScore < maxScore && newScores[column][row] < maxScore) {
          newErrors.push([row, column])
          hasError = true
        }
      }

      if (!hasError) {
        newPool = calculateResults(newPool, newScores, props?.config?.score, pointsSettings)
        setPool(newPool)

        let r = column < props?.pool?.participants?.length - 1 ? row : row + 1
        let c = column < props?.pool?.participants?.length - 1 ? column + 1 : 0

        if (r === c) c += 1
        if (pool?.participants?.[r]?.status && r < pool?.participants?.length - 1) r += 1
        if (pool?.participants?.[c]?.status) c += 1

        if (r === c) c += 1
      }
    } else {
      newScores[row][column] = null
    }

    setErrors(newErrors)
    setScores(prevState => {
      return newScores
    })
    newPool.scores = newScores
    props.onPoolScore(Object.assign({}, newPool, { advanced: advancedStats }))
  }

  const handleKeyPress = (row, column, event) => {
    if (event.key === 'Backspace' && event.target.value.length === 0) {
      event.preventDefault()
      let r = column > 1 ? row : row - 1
      let c = column > 1 ? column - 1 : pool?.participants?.length - 1

      if (r === c) c -= 1
      if (pool?.participants?.[r]?.status && r > 1) r -= 1
      if (pool?.participants?.[c]?.status) c -= 1

      if (r === c) c -= 1

      const nextFieldName = `field_${r}_${c}`
      const nextfield = document.querySelector(
        `input[name=${nextFieldName}]`
      )
      if (nextfield !== null) {
        nextfield.focus()
      }
    }
  }

  const handleOnScoreFocus = (row, column, event) => {
    // event?.target?.select()
    setFocusedMatch([row, column])
  }

  const handleStatusChange = (row, value) => {
    let newPool = JSON.parse(JSON.stringify(pool))
    const newScores = JSON.parse(JSON.stringify(scores))
    newPool.participants[row].status = value
    newPool = calculateResults(newPool, newScores, props?.config?.score, pointsSettings)
    setPool(newPool)
  }

  const handleAdvancedPoints = (event, match, p1, p2) => {
    if (!checkValidAttack(event)) return

    const newScores = JSON.parse(JSON.stringify(scores))
    const newAdvancedStats = JSON.parse(JSON.stringify(advancedStats))
    let newPool = JSON.parse(JSON.stringify(pool))
    let current = newScores[p1 - 1][p2 - 1] || 0
    if (current === 'V') current = props.config.score
    if (_.first(current) === 'V') current = parseInt(current.slice(1), 10)

    let attack
    let isRemove = false

    if (['Backspace', 'Delete'].includes(event.key)) {
      const position = current > props.config.score ? props.config.score : current

      const baseAttack = attackType[_.toLower(newAdvancedStats[match][p1][position - 1])]
      attack = {
        value: baseAttack.value
      }
      current += -1 * baseAttack.value
      isRemove = true
    } else if (event.key === 'Tab') {
      event.preventDefault()
      let currentN = matchData[pool?.participants?.length][match][0]
      let currentM = match
      if (currentN === p1) {
        currentN = matchData[pool?.participants?.length][match][1]
      } else if (currentN === p2) {
        currentN = matchData[pool?.participants?.length][match + 1][0]
        currentM = match + 1
      }

      const nextFieldName = `m${currentM + 1}_${currentN}_1`
      const nextfield = document.querySelector(
        `input[name=${nextFieldName}]`
      )
      if (nextfield !== null) {
        nextfield.focus()
      }
      return
    } else {
      attack = attackType[_.toLower(event.key)]
    }

    let victoryScore = props.config.score
    if (_.first(newScores[p2 - 1][p1 - 1]) === 'V' && newScores[p2 - 1][p1 - 1].length > 1) {
      victoryScore = parseInt(newScores[p2 - 1][p1 - 1].slice(1), 10)
    }

    if (current + attack.value >= victoryScore && _.first(newScores[p2 - 1][p1 - 1]) === 'V') {
      // do nothing
    } else {
      for (let i = 0; i < attack.value; i += 1) {
        if (isRemove) {
          newAdvancedStats[match][p1][current + i] = null
        } else {
          if (current + i < props.config.score) {
            if (current + i === props.config.score - 1) {
              newAdvancedStats[match][p1][current + i] = _.toUpper(event.key)
            } else {
              if (i + 1 === attack.value) {
                newAdvancedStats[match][p1][current + i] = _.toUpper(event.key)
              } else {
                newAdvancedStats[match][p1][current + i] = 'x'
              }
            }
          }
        }
      }

      let newScore = newScores[p1 - 1][p2 - 1]
      if (newScore === 'V') {
        newScore = props.config.score
      } else if (_.first(newScore) === 'V') {
        newScore = parseInt(newScore.slice(1), 10)
      }
      if (isRemove) {
        newScore -= attack.value
      } else {
        if (_.first(newScore) !== 'V') {
          newScore += attack.value
        }
      }

      if (newScore >= props.config.score && _.first(newScores[p2 - 1][p1 - 1]) !== 'V') {
        if (newScore === props.config.score) {
          newScore = 'V'
        } else {
          newScore = `V${newScore}`
        }
      } else if (newScore >= props.config.score && _.first(newScores[p2 - 1][p1 - 1]) === 'V') {
        if (newScores[p2 - 1][p1 - 1].length > 1) {
          newScore = parseInt(newScores[p2 - 1][p1 - 1].slice(1), 10) - 1
        } else {
          newScore = props.config.score - 1
        }
      }

      newScores[p1 - 1][p2 - 1] = newScore
    }

    newPool = calculateResults(newPool, newScores, props?.config?.score, pointsSettings)
    setPool(newPool)
    setScores(newScores)
    setAdvancedStats(newAdvancedStats)
    props.onPoolScore(Object.assign({}, newPool, { advanced: newAdvancedStats }))
  }

  const handleVictory = (e, p1, p2) => {
    const newScores = JSON.parse(JSON.stringify(scores))
    let newPool = JSON.parse(JSON.stringify(pool))

    const current = newScores[p1 - 1][p2 - 1] || 0

    if (_.first(current) === 'V') {
      if (!e.target.checked) {
        newScores[p1 - 1][p2 - 1] = _.toNumber(_.trim(current, 'V'))
      }
    } else {
      newScores[p1 - 1][p2 - 1] = `V${current}`
      if (_.first(newScores[p2 - 1][p1 - 1]) === 'V') {
        newScores[p2 - 1][p1 - 1] = _.toNumber(_.trim(newScores[p2 - 1][p1 - 1], 'V'))
      } else {
        newScores[p2 - 1][p1 - 1] = _.toNumber(newScores[p2 - 1][p1 - 1])
      }
    }

    setScores(newScores)
    newPool = calculateResults(newPool, newScores, props?.config?.score, pointsSettings)
    newPool.scores = newScores
    props.onPoolScore(Object.assign({}, newPool, { advanced: advancedStats }))
  }

  return (
    <>
      <div style={{ position: 'relative' }}>
        <div className='score-grid-title'><h1>{squadronHelper.getColor(pool?.index)} Squadron</h1></div>
        <Row>
          <Col span={12}>
            <div className='score-grid'>
              <Row className='score-grid-label'>
                <Col span={Math.floor(24 - (pool?.participants?.length * 2))}> </Col>
                {scores.map((s, sindex) =>
                  <Col key={`p${sindex}`} span={2}>
                    {sindex + 1}
                  </Col>
                )}
              </Row>
              {scores.map((s, sindex) =>
                <Row key={`s${sindex}`}>
                  <Col span={Math.floor(24 - (pool?.participants?.length * 2))} key={`p${sindex}`} className='grid-row-label'>
                    <span>{sindex + 1}</span><span>{props?.pool?.participants?.[sindex]?.last_name}, {_.first(props?.pool?.participants?.[sindex]?.first_name)}</span>
                  </Col>
                  {s.map((p, pindex) =>
                    <Col span={2} key={`p${pindex}`} className={`grid-cell ${sindex === pindex || pool.participants[sindex].status || pool.participants[pindex].status ? 'blank-grid-cell' : ''}`}>
                      {sindex !== pindex && !pool.participants[sindex].status && !pool.participants[pindex].status ? <Input value={scores?.[sindex]?.[pindex]} name={`field_${sindex}_${pindex}`} className={`${isErrored(sindex, pindex) ? 'has-error' : ''} ${isFocusedMatch(sindex, pindex) ? 'focused-match' : ''}`} onFocus={(e) => handleOnScoreFocus(sindex, pindex, e)} onChange={(e) => { handleScore(sindex, pindex, e) }} onKeyDown={(e) => handleKeyPress(sindex, pindex, e)} placeholder={getMatchNumber(sindex, pindex)} /> : ''}
                    </Col>
                  )}
                </Row>
              )}
            </div>
          </Col>
          <Col span={12}>
            <div className='score-grid-row-results'>
              <Row className='score-grid-row-results-header'>
                <Col span={3}>
                  <div>Status</div>
                </Col>
                <Col span={3}>
                  <div>Victories</div>
                </Col>
                <Col span={3}>
                  <div>Points</div>
                </Col>
                <Col span={3}>
                  <div>Hits Scored</div>
                </Col>
                <Col span={3}>
                  <div>Hits Received</div>
                </Col>
                <Col span={3}>
                  <div>Index</div>
                </Col>
                <Col span={3}>
                  <div>Place</div>
                </Col>
              </Row>
              {scores.map((s, sindex) =>
                <Row key={`s${sindex}`} className='score-grid-row-results-values'>
                  <Col span={3}>
                    <Select defaultValue={0} value={!pool?.participants?.[sindex]?.status ? 0 : pool?.participants?.[sindex]?.status} style={{ width: '4em' }} onChange={(e) => { handleStatusChange(sindex, e) }}>
                      <Select.Option value={0}><FontAwesomeIcon icon={faCheck} style={{ color: 'green' }} /></Select.Option>
                      <Select.Option value={1}><FontAwesomeIcon icon={faSuitcaseMedical} style={{ color: 'blue' }} /></Select.Option>
                      <Select.Option value={2}><FontAwesomeIcon icon={faSquare} style={{ color: 'black' }} /></Select.Option>
                    </Select>
                  </Col>
                  <Col span={3}>
                    {pool?.participants?.[sindex]?.stats?.victories || 0}
                  </Col>
                  <Col span={3}>
                    {pool?.participants?.[sindex]?.stats?.points || 0}
                  </Col>
                  <Col span={3}>
                    {pool?.participants?.[sindex]?.stats?.hitsScored || 0}
                  </Col>
                  <Col span={3}>
                    {pool?.participants?.[sindex]?.stats?.hitsAgainst || 0}
                  </Col>
                  <Col span={3}>
                    {pool?.participants?.[sindex]?.stats?.index || 0}
                  </Col>
                  <Col span={3}>
                    {pool?.participants?.[sindex]?.stats?.place}
                  </Col>
                </Row>
              )}
            </div>
          </Col>
        </Row>
      </div>
      {props.advancedStats
        ? (
          <div className='score-grid-match-list'>
            <Row gutter={16}>
              {matchData[pool?.participants?.length]?.map((m, index) =>
                !pool?.participants[m[0] - 1].status && !pool?.participants[m[1] - 1].status
                  ? (
                    <Col xl={12} key={`score_${index}`} className='score-grid-match-list-item' style={{ paddingBottom: '1em' }}>
                      <Row>
                        <Col span={2} className='score-grid-match-number'>{`M${index + 1}`}</Col>
                        <Col span={22}>
                          <Row>
                            <Col span={3} style={{ borderTop: '1px solid #000', borderBottom: '1px solid #000', textAlign: 'right', paddingRight: '2em', color: '#fff', backgroundColor: '#000' }}>#</Col>
                            <Col span={8} style={{ borderTop: '1px solid #000', borderBottom: '1px solid #000', color: '#fff', backgroundColor: '#000' }}>Name</Col>
                            <Col span={13}>
                              <Row>
                                {_.times(props?.config?.score, (num) =>
                                  <Col span={Math.floor(24 / props?.config?.score - 1)} style={{ textAlign: 'center', border: '1px solid #000', color: '#fff', backgroundColor: '#000' }}>
                                    {num + 1}
                                  </Col>
                                )}
                                <Col span={Math.floor(24 / props?.config?.score - 1)} style={{ textAlign: 'center', border: '1px solid #000', color: '#fff', backgroundColor: '#000' }}>
                                  V
                                </Col>
                              </Row>
                            </Col>
                          </Row>
                          <Row>
                            <Col span={3} style={{ borderBottom: '1px solid #000', textAlign: 'right', paddingRight: '2em' }}>{m[0]}</Col>
                            <Col span={8} style={{ borderBottom: '1px solid #000' }}>{pool.participants[m[0] - 1].last_name}</Col>
                            <Col span={13}>
                              <Row>
                                {_.times(props?.config?.score, (num) =>
                                  <Col span={Math.floor(24 / props?.config?.score - 1)}>
                                    <Input name={`m${index + 1}_${m[0]}_${num + 1}`} className={advancedStats?.[index]?.[m[0]]?.[num] === 'x' ? 'disabled-advanced' : ''} onKeyDown={(e) => handleAdvancedPoints(e, index, m[0], m[1], num)} value={advancedStats?.[index]?.[m[0]]?.[num]} />
                                  </Col>
                                )}
                                <Col span={Math.floor(24 / props?.config?.score - 1)} style={{ textAlign: 'center', marginTop: '.25em' }}>
                                  <Checkbox name={`${m[0]}_${m[1]}`} checked={_.first(scores?.[m[0] - 1]?.[m[1] - 1]) === 'V'} onChange={(e) => handleVictory(e, m[0], m[1])} />
                                </Col>
                              </Row>
                            </Col>
                          </Row>
                          <Row>
                            <Col span={3} style={{ borderBottom: '1px solid #000', textAlign: 'right', paddingRight: '2em' }}>{m[1]}</Col>
                            <Col span={8} style={{ borderBottom: '1px solid #000' }}>{pool.participants[m[1] - 1].last_name}</Col>
                            <Col span={13}>
                              <Row>
                                {_.times(props?.config?.score, (num) =>
                                  <Col span={Math.floor(24 / props?.config?.score - 1)}>
                                    <Input name={`m${index + 1}_${m[1]}_${num + 1}`} className={advancedStats?.[index]?.[m[1]]?.[num] === 'x' ? 'disabled-advanced' : ''} onKeyDown={(e) => handleAdvancedPoints(e, index, m[1], m[0], num)} value={advancedStats?.[index]?.[m[1]]?.[num]} />
                                  </Col>
                                )}
                                <Col span={Math.floor(24 / props?.config?.score - 1)} style={{ textAlign: 'center', marginTop: '.25em' }}>
                                  <Checkbox name={`${m[1]}_${m[0]}`} checked={_.first(scores?.[m[1] - 1]?.[m[0] - 1]) === 'V'} onChange={(e) => handleVictory(e, m[1], m[0])} />
                                </Col>
                              </Row>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    </Col>
                    )
                  : ''
              )}
            </Row>
          </div>
          )
        : ''}
    </>
  )
}

export default PoolScoreMatrix
